import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  signUp: (email: string, password: string) => Promise<void>;
}

const ONE_HOUR_IN_MS = 3600000; // 1 hour in milliseconds

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const backendUrl = window.location.hostname.includes("localhost")
    ? "http://localhost:50666"
    : window.location.origin;

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem("userData");
  }, []);
  const onNewToken = useCallback(
    (newToken: string, expiration: number) => {
      setToken(newToken);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          token: newToken,
          expiration: expiration,
        })
      );
      const remainingTime = expiration - new Date().getTime();
      setTimeout(() => {
        logout(); // Automatically log out when the token expires
      }, remainingTime);
    },
    [logout]
  );

  const login = async (email: string, password: string) => {
    const res = await fetch(backendUrl + "/api/user/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      console.error(data.message);
      throw new Error(data.message); // Rethrow the error to be handled by the caller
    }
    onNewToken(data.token, new Date().getTime() + ONE_HOUR_IN_MS);
  };
  const signUp = async (email: string, password: string) => {
    const res = await fetch(backendUrl + "/api/user/signup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      console.error(data.message);
      throw new Error(data.message); // Rethrow the error to be handled by the caller
    }
    onNewToken(data.token, new Date().getTime() + ONE_HOUR_IN_MS);
  };

  useEffect(() => {
    try {
      const validateToken = async (token: string) => {
        try {
          const res = await fetch(backendUrl + "/api/user/validate", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (!res.ok) {
            console.error("Token validation failed");
            logout();
          }
        } catch (error) {
          console.error("Error validating token:", error);
          logout();
        }
      };
      const storedData = localStorage.getItem("userData");
      if (storedData) {
        const { token, expiration } = JSON.parse(storedData);
        validateToken(token);
        onNewToken(token, expiration);
      }
    } catch (error) {
      console.error("Error parsing token from localStorage:", error);
      logout();
    }
  }, [onNewToken, logout, backendUrl]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!token, token, login, logout, signUp }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
